import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiRequest } from '../utils/api';
import './MarketingConsentModal.scss';

type Props = {
  onClose: () => void;
};

const CheckIcon: React.FC = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 22 22"
    fill="none"
    aria-hidden="true"
  >
    <circle cx="11" cy="11" r="11" fill="#86f4ca" />
    <path
      d="M6 11.5l3.2 3.2L16 8"
      stroke="#0a3f6a"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MarketingConsentModal: React.FC<Props> = ({ onClose }) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState<null | 'accept' | 'decline'>(null);

  const submit = async (value: boolean, action: 'accept' | 'decline') => {
    if (submitting) return;
    setSubmitting(action);
    try {
      await apiRequest('/marketing-consent', {
        method: 'PUT',
        body: { marketing_consent: value },
      });
    } catch (err) {
      // Non-blocking: don't trap the user behind a backend hiccup. The next
      // login still shows the modal because marketing_consent_shown stays
      // false on the server when this request fails.
      console.error('Failed to save marketing consent:', err);
    } finally {
      setSubmitting(null);
      onClose();
    }
  };

  const benefits = [
    t('marketingConsentModal.benefits.first'),
    t('marketingConsentModal.benefits.second'),
    t('marketingConsentModal.benefits.third'),
  ];

  return (
    <div className="marketing-consent-modal-overlay">
      <div
        className="marketing-consent-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="marketing-consent-modal__title">
          {t('marketingConsentModal.headerTitle')}
        </div>

        <img
          className="marketing-consent-modal__illustration"
          src="/assets/naama5.png"
          alt=""
          aria-hidden="true"
        />

        <h2 className="marketing-consent-modal__headline">
          {t('marketingConsentModal.headline')}
        </h2>

        <ul className="marketing-consent-modal__benefits">
          {benefits.map((text) => (
            <li key={text} className="marketing-consent-modal__benefit">
              <span className="marketing-consent-modal__benefit-icon">
                <CheckIcon />
              </span>
              <span className="marketing-consent-modal__benefit-text">
                {text}
              </span>
            </li>
          ))}
        </ul>

        <p className="marketing-consent-modal__subtext">
          {t('marketingConsentModal.subtext')}
        </p>

        <div className="marketing-consent-modal__actions">
          <button
            type="button"
            className="marketing-consent-modal__decline"
            onClick={() => submit(false, 'decline')}
            disabled={!!submitting}
          >
            {t('marketingConsentModal.decline')}
          </button>
          <button
            type="button"
            className="marketing-consent-modal__accept"
            onClick={() => submit(true, 'accept')}
            disabled={!!submitting}
          >
            {t('marketingConsentModal.accept')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MarketingConsentModal;
